import React from 'react';
import classNames from 'classnames';
import { twMerge } from 'tailwind-merge';
import { useTranslation } from 'react-i18next';
import Footer from '../misc/Footer';
import DownloadButton from '../misc/DownloadButton';
import NavigationBar from '../misc/NavigationBar';

interface CommonPageWrapperProps {
  children: React.ReactNode;
  includeCommon?: boolean;
  containerClassName?: string;
}
function CommonPageWrapper({
  children,
  includeCommon = true,
  containerClassName,
}: CommonPageWrapperProps): JSX.Element {
  const { t } = useTranslation();

  const title = React.Children.toArray(children).find((child) => React.isValidElement(child) && child.type === Title);
  const message = React.Children.toArray(children).find(
    (child) => React.isValidElement(child) && child.type === Message,
  );
  const body = React.Children.toArray(children).find((child) => React.isValidElement(child) && child.type === Body);

  const other = React.Children.toArray(children).filter(
    (child) =>
      React.isValidElement(child) &&
      child.type !== Title &&
      child.type !== Message &&
      child.type !== Header &&
      child.type !== Body,
  );

  const header = React.Children.toArray(children).find((child) => React.isValidElement(child) && child.type === Header);
  return (
    <div className="flex min-h-screen flex-col">
      <NavigationBar />
      <div className="flex-1 bg-secondary-50">
        <div className="display-stretch mx-auto my-8 flex w-full max-w-7xl flex-col-reverse gap-8 rounded-2xl bg-secondary-50 px-4 md:flex-row">
          {/* Success image */}
          {header}

          {/* Message and download banners */}
          <div className={twMerge('flex h-fit flex-col pt-4 md:w-[70%]', containerClassName)}>
            <div
              className={classNames('border-secondary-50', {
                'mb-10 border-b pb-10': other.length > 0 && includeCommon,
              })}>
              {title}
              {message}
              {body}
              {includeCommon && (
                <>
                  <p className="my-4 text-base font-normal">{t('page.common.message')}</p>
                  <DownloadButton />
                </>
              )}
            </div>
            {other}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

function Title({ children }: { children: React.ReactNode }): JSX.Element {
  return <h1 className="mb-4 text-2xl font-semibold">{children}</h1>;
}

function Message({ children }: { children: React.ReactNode }): JSX.Element {
  return <h2 className="mt-2 text-2xl font-[600px]">{children}</h2>;
}

function Body({ children }: { children: React.ReactNode }): JSX.Element {
  return <>{children}</>;
}

interface HeaderProps {
  children: React.ReactNode;
}

function Header({ children }: HeaderProps): JSX.Element {
  return <>{children}</>;
}

CommonPageWrapper.Title = Title;
CommonPageWrapper.Message = Message;
CommonPageWrapper.Header = Header;
CommonPageWrapper.Body = Body;

export default CommonPageWrapper;
